import React, { useCallback, useEffect, useMemo, useState } from "react";
import DashboardLayout from "../../Components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../Components/Navbars/DashboardNavbar";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { Card, Grid, FormControl, Select, MenuItem } from "@mui/material";
import MDTypography from "../../Components/MDTypography";
import AppLoading from "../../Components/MDLoading";
import MDBox from "../../Components/MDBox";
import ComplexStatisticsCard from "../../Components/Cards/StatisticsCards/ComplexStatisticsCard";
import { BillModel } from "../../Api/Models/bill";
import { TarefeModel } from "../../Api/Models/Tarefe";
import { VehicleModel } from "../../Api/Models/Vehicle";
import { DateObject } from "react-multi-date-picker";
import gregorian from "react-date-object/calendars/gregorian";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";

const PERSIAN_MONTH_NAMES = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
];

const PIE_COLORS = ["#4caf50", "#f44336", "#fb8c00", "#1A73E8"];
const BAR_COLORS = [
  "#1A73E8",
  "#7b1fa2",
  "#00bcd4",
  "#ff9800",
  "#4caf50",
  "#e91e63",
  "#607d8b",
];

const formatNumber = (num: number | string | undefined | null) => {
  if (num === undefined || num === null) return "۰";
  return Number(num).toLocaleString("fa-IR");
};

const formatCurrency = (num: number | string | undefined | null) => {
  if (num === undefined || num === null) return "۰ ریال";
  return `${Number(num).toLocaleString("fa-IR")} ریال`;
};

const getPersianDateInfo = (dateStr: string) => {
  if (!dateStr) return { monthIndex: -1, year: "" };
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return { monthIndex: -1, year: "" };
    const dateObj = new DateObject({
      calendar: gregorian,
      date: d,
      locale: persian_fa,
    }).convert(persian);
    return {
      monthIndex: dateObj.month.number - 1,
      monthName: dateObj.month.name,
      year: dateObj.year.toString(),
    };
  } catch {
    return { monthIndex: -1, year: "" };
  }
};

const ChartCard = ({
  title,
  subtitle,
  children,
  action,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) => (
  <Card
    sx={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      borderRadius: "12px",
      boxShadow: "0 4px 20px 0 rgba(0, 0, 0, 0.05)",
      border: "1px solid #e8e8e8",
      overflow: "hidden",
    }}
  >
    <MDBox
      p={2}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        borderBottom: "1px solid #f0f0f0",
        backgroundColor: "#fafafa",
      }}
    >
      <MDBox>
        <MDTypography variant="h6" fontWeight="bold" fontSize="15px" color="dark">
          {title}
        </MDTypography>
        {subtitle && (
          <MDTypography variant="caption" color="text" fontSize="12px" display="block">
            {subtitle}
          </MDTypography>
        )}
      </MDBox>
      {action && <MDBox>{action}</MDBox>}
    </MDBox>
    <MDBox p={2} flex={1} sx={{ minHeight: 300, position: "relative" }}>
      {children}
    </MDBox>
  </Card>
);

const MonthlyTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <Card sx={{ p: 1.5, minWidth: 170, boxShadow: 3, border: "1px solid #e0e0e0" }}>
        <MDTypography variant="button" fontWeight="bold" color="dark" mb={1} display="block">
          ماه {label}
        </MDTypography>
        {payload.map((entry: any, index: number) => (
          <MDBox key={index} display="flex" justifyContent="space-between" alignItems="center" my={0.5}>
            <MDTypography variant="caption" sx={{ color: entry.color, fontWeight: "medium" }}>
              {entry.name}:
            </MDTypography>
            <MDTypography variant="caption" fontWeight="bold">
              {formatNumber(entry.value)}
            </MDTypography>
          </MDBox>
        ))}
      </Card>
    );
  }
  return null;
};

const PieTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const item = payload[0].payload;
    return (
      <Card sx={{ p: 1.5, minWidth: 190, boxShadow: 3, border: "1px solid #e0e0e0" }}>
        <MDBox display="flex" alignItems="center" mb={1}>
          <MDBox
            width={12}
            height={12}
            borderRadius="50%"
            mr={1}
            sx={{ backgroundColor: item.color }}
          />
          <MDTypography variant="button" fontWeight="bold" color="dark">
            {item.name}
          </MDTypography>
        </MDBox>
        <MDBox display="flex" justifyContent="space-between" my={0.5}>
          <MDTypography variant="caption" color="text">
            تعداد قبوض:
          </MDTypography>
          <MDTypography variant="caption" fontWeight="bold">
            {formatNumber(item.value)}
          </MDTypography>
        </MDBox>
        <MDBox display="flex" justifyContent="space-between" my={0.5}>
          <MDTypography variant="caption" color="text">
            درصد:
          </MDTypography>
          <MDTypography variant="caption" fontWeight="bold">
            {formatNumber(item.percent)}%
          </MDTypography>
        </MDBox>
        <MDBox display="flex" justifyContent="space-between" my={0.5}>
          <MDTypography variant="caption" color="text">
            مبلغ کل:
          </MDTypography>
          <MDTypography variant="caption" fontWeight="bold" color="success">
            {formatCurrency(item.amount)}
          </MDTypography>
        </MDBox>
      </Card>
    );
  }
  return null;
};

const TariffTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const item = payload[0].payload;
    return (
      <Card sx={{ p: 1.5, minWidth: 210, boxShadow: 3, border: "1px solid #e0e0e0" }}>
        <MDTypography variant="button" fontWeight="bold" color="dark" mb={1} display="block">
          {item.name}
        </MDTypography>
        <MDBox display="flex" justifyContent="space-between" my={0.5}>
          <MDTypography variant="caption" color="text">
            تعداد استفاده:
          </MDTypography>
          <MDTypography variant="caption" fontWeight="bold">
            {formatNumber(item.count)} بار
          </MDTypography>
        </MDBox>
        <MDBox display="flex" justifyContent="space-between" my={0.5}>
          <MDTypography variant="caption" color="text">
            مبلغ کل:
          </MDTypography>
          <MDTypography variant="caption" fontWeight="bold" color="primary">
            {formatCurrency(item.totalAmount)}
          </MDTypography>
        </MDBox>
      </Card>
    );
  }
  return null;
};

const YearlyTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const item = payload[0].payload;
    return (
      <Card sx={{ p: 1.5, minWidth: 210, boxShadow: 3, border: "1px solid #e0e0e0" }}>
        <MDTypography variant="button" fontWeight="bold" color="dark" mb={1} display="block">
          سال {label}
        </MDTypography>
        <MDBox display="flex" justifyContent="space-between" my={0.5}>
          <MDTypography variant="caption" color="info">
            کل قبوض:
          </MDTypography>
          <MDTypography variant="caption" fontWeight="bold">
            {formatNumber(item.totalBills)}
          </MDTypography>
        </MDBox>
        <MDBox display="flex" justifyContent="space-between" my={0.5}>
          <MDTypography variant="caption" color="success">
            پرداخت‌شده:
          </MDTypography>
          <MDTypography variant="caption" fontWeight="bold">
            {formatNumber(item.paidBills)}
          </MDTypography>
        </MDBox>
        <MDBox display="flex" justifyContent="space-between" my={0.5}>
          <MDTypography variant="caption" color="text">
            مبلغ کل عوارض:
          </MDTypography>
          <MDTypography variant="caption" fontWeight="bold">
            {formatCurrency(item.totalAmount)}
          </MDTypography>
        </MDBox>
        <MDBox display="flex" justifyContent="space-between" my={0.5}>
          <MDTypography variant="caption" color="text">
            مبلغ وصول‌شده:
          </MDTypography>
          <MDTypography variant="caption" fontWeight="bold" color="success">
            {formatCurrency(item.paidAmount)}
          </MDTypography>
        </MDBox>
      </Card>
    );
  }
  return null;
};

export default function MainPage() {
  const [loading, setLoading] = useState(true);
  const [bills, setBills] = useState<any[]>([]);
  const [tariffs, setTariffs] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>("all");

  const billModel = useMemo(() => new BillModel(), []);
  const tarefeModel = useMemo(() => new TarefeModel(), []);
  const vehicleModel = useMemo(() => new VehicleModel(), []);

  useEffect(() => {
    setLoading(true);
    Promise.allSettled([
      billModel.GetAll(),
      tarefeModel.GetAll(),
      vehicleModel.GetAll(),
    ])
      .then(([billsRes, tarefeRes, vehicleRes]) => {
        if (billsRes.status === "fulfilled" && billsRes.value?.data?.data) {
          setBills(billsRes.value.data.data);
        }
        if (tarefeRes.status === "fulfilled" && tarefeRes.value?.data?.data) {
          setTariffs(tarefeRes.value.data.data);
        }
        if (vehicleRes.status === "fulfilled" && vehicleRes.value?.data?.data) {
          setVehicles(vehicleRes.value.data.data);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [billModel, tarefeModel, vehicleModel]);

  // Extract available years from bills
  const availableYears = useMemo(() => {
    const yearSet = new Set<string>();
    bills.forEach((b) => {
      if (b.year) yearSet.add(b.year.toString());
      const pInfo = getPersianDateInfo(b.issueDate);
      if (pInfo.year) yearSet.add(pInfo.year);
    });
    return Array.from(yearSet).sort((a, b) => b.localeCompare(a));
  }, [bills]);

  // Filtered bills based on selected year
  const filteredBills = useMemo(() => {
    if (selectedYear === "all") return bills;
    return bills.filter((b) => {
      if (b.year && b.year.toString() === selectedYear) return true;
      const pInfo = getPersianDateInfo(b.issueDate);
      return pInfo.year === selectedYear;
    });
  }, [bills, selectedYear]);

  // 1. KPI Statistics
  const stats = useMemo(() => {
    const totalCount = bills.length;
    const paidCount = bills.filter((b) => b.isPaid).length;
    const unpaidCount = totalCount - paidCount;

    const totalAmount = bills.reduce(
      (acc, b) => acc + (Number(b.totalAmount) || 0),
      0
    );
    const paidAmount = bills
      .filter((b) => b.isPaid)
      .reduce((acc, b) => acc + (Number(b.totalAmount) || 0), 0);
    const unpaidAmount = totalAmount - paidAmount;

    const paidRate = totalCount > 0 ? Math.round((paidCount / totalCount) * 100) : 0;

    return {
      totalCount,
      paidCount,
      unpaidCount,
      totalAmount,
      paidAmount,
      unpaidAmount,
      paidRate,
    };
  }, [bills]);

  // 2. Chart 1: Monthly Issuance & Payment Trend
  const monthlyChartData = useMemo(() => {
    const monthlyList = PERSIAN_MONTH_NAMES.map((name, index) => ({
      name,
      monthIndex: index,
      issuedCount: 0,
      paidCount: 0,
      totalAmount: 0,
      paidAmount: 0,
    }));

    filteredBills.forEach((b) => {
      const pInfo = getPersianDateInfo(b.issueDate);
      if (pInfo.monthIndex >= 0 && pInfo.monthIndex < 12) {
        monthlyList[pInfo.monthIndex].issuedCount += 1;
        monthlyList[pInfo.monthIndex].totalAmount += Number(b.totalAmount) || 0;
        if (b.isPaid) {
          monthlyList[pInfo.monthIndex].paidCount += 1;
          monthlyList[pInfo.monthIndex].paidAmount += Number(b.totalAmount) || 0;
        }
      }
    });

    return monthlyList;
  }, [filteredBills]);

  // 3. Chart 2: Payment Status Breakdown (Paid vs Unpaid)
  const paymentStatusData = useMemo(() => {
    const total = filteredBills.length;
    const paidBills = filteredBills.filter((b) => b.isPaid);
    const unpaidBills = filteredBills.filter((b) => !b.isPaid);

    const paidAmount = paidBills.reduce(
      (acc, b) => acc + (Number(b.totalAmount) || 0),
      0
    );
    const unpaidAmount = unpaidBills.reduce(
      (acc, b) => acc + (Number(b.totalAmount) || 0),
      0
    );

    const paidPercent = total > 0 ? Math.round((paidBills.length / total) * 100) : 0;
    const unpaidPercent = total > 0 ? 100 - paidPercent : 0;

    return [
      {
        name: "پرداخت شده",
        value: paidBills.length,
        amount: paidAmount,
        percent: paidPercent,
        color: "#4caf50",
      },
      {
        name: "در انتظار پرداخت",
        value: unpaidBills.length,
        amount: unpaidAmount,
        percent: unpaidPercent,
        color: "#f44336",
      },
    ];
  }, [filteredBills]);

  // 4. Chart 3: Tariff Distribution / Usage
  const tariffChartData = useMemo(() => {
    const tariffMap = new Map<
      string,
      { name: string; count: number; totalAmount: number }
    >();

    // Index tariffs by id
    const tariffLookup = new Map<number, string>();
    tariffs.forEach((t) => {
      tariffLookup.set(t.id, t.title);
    });

    bills.forEach((b) => {
      if (b.items && Array.isArray(b.items) && b.items.length > 0) {
        b.items.forEach((item: any) => {
          const title =
            item.tariffTitle ||
            tariffLookup.get(item.tariffId) ||
            "تعرفه عمومی";
          const current = tariffMap.get(title) || {
            name: title,
            count: 0,
            totalAmount: 0,
          };
          current.count += 1;
          current.totalAmount += Number(item.amount) || 0;
          tariffMap.set(title, current);
        });
      }
    });

    // If bills items are not populated, populate from tariffs list
    if (tariffMap.size === 0 && tariffs.length > 0) {
      tariffs.slice(0, 6).forEach((t) => {
        tariffMap.set(t.title, {
          name: t.title,
          count: 0,
          totalAmount: Number(t.fixedAmount) || 0,
        });
      });
    }

    const result = Array.from(tariffMap.values());
    result.sort((a, b) => b.totalAmount - a.totalAmount || b.count - a.count);
    return result.slice(0, 6);
  }, [bills, tariffs]);

  // 5. Chart 4: Yearly Comparison
  const yearlyChartData = useMemo(() => {
    const yearMap = new Map<
      string,
      {
        year: string;
        totalBills: number;
        paidBills: number;
        totalAmount: number;
        paidAmount: number;
      }
    >();

    bills.forEach((b) => {
      let y = b.year ? b.year.toString() : "";
      if (!y) {
        const pInfo = getPersianDateInfo(b.issueDate);
        y = pInfo.year || "۱۴۰۵";
      }
      const current = yearMap.get(y) || {
        year: y,
        totalBills: 0,
        paidBills: 0,
        totalAmount: 0,
        paidAmount: 0,
      };
      current.totalBills += 1;
      current.totalAmount += Number(b.totalAmount) || 0;
      if (b.isPaid) {
        current.paidBills += 1;
        current.paidAmount += Number(b.totalAmount) || 0;
      }
      yearMap.set(y, current);
    });

    const result = Array.from(yearMap.values());
    result.sort((a, b) => a.year.localeCompare(b.year));
    return result;
  }, [bills]);

  return (
    <DashboardLayout>
      <DashboardNavbar name={"داشبورد و آمار عوارض حمل و نقل"} />
      <AppLoading show={loading} />

      {!loading && (
        <MDBox py={2}>
          {/* Top KPI Summary Cards */}
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} sm={6} md={3}>
              <ComplexStatisticsCard
                color="info"
                icon="receipt"
                title="کل قبوض صادره عوارض"
                count={formatNumber(stats.totalCount)}
                percentage={{
                  color: "info",
                  amount: `${formatNumber(filteredBills.length)}`,
                  label: "قبض در دوره انتخابی",
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ComplexStatisticsCard
                color="success"
                icon="check_circle"
                title="مبالغ وصول‌شده"
                count={formatCurrency(stats.paidAmount)}
                percentage={{
                  color: "success",
                  amount: `${formatNumber(stats.paidRate)}%`,
                  label: "نرخ وصول قبوض",
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ComplexStatisticsCard
                color="warning"
                icon="pending_actions"
                title="مبالغ در انتظار پرداخت"
                count={formatCurrency(stats.unpaidAmount)}
                percentage={{
                  color: "error",
                  amount: `${formatNumber(stats.unpaidCount)}`,
                  label: "قبض پرداخت‌نشده",
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ComplexStatisticsCard
                color="primary"
                icon="local_taxi"
                title="تعرفه‌ها و وسایل نقلیه"
                count={`${formatNumber(tariffs.length)} تعرفه`}
                percentage={{
                  color: "primary",
                  amount: `${formatNumber(vehicles.length)}`,
                  label: "نوع وسیله نقلیه ثبت‌شده",
                }}
              />
            </Grid>
          </Grid>

          {/* 4 Specialized Charts for عوارض حمل و نقل */}
          <Grid container spacing={3}>
            {/* Chart 1: روند ماهانه صدور و پرداخت عوارض */}
            <Grid item xs={12} lg={6}>
              <ChartCard
                title="روند ماهانه صدور و پرداخت عوارض"
                subtitle="مقایسه تعداد قبوض صادرشده و پرداخت‌شده در ماه‌های سال"
                action={
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      displayEmpty
                      sx={{ fontSize: "13px", height: "32px" }}
                    >
                      <MenuItem value="all">همه سال‌ها</MenuItem>
                      {availableYears.map((yr) => (
                        <MenuItem key={yr} value={yr}>
                          سال {yr}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                }
              >
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart
                    data={monthlyChartData}
                    margin={{ top: 15, right: 10, left: 10, bottom: 25 }}
                  >
                    <defs>
                      <linearGradient id="colorIssued" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1A73E8" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#1A73E8" stopOpacity={0.0} />
                      </linearGradient>
                      <linearGradient id="colorPaid" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#4caf50" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#4caf50" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="name"
                      interval={0}
                      angle={-30}
                      textAnchor="end"
                      fontSize={11}
                      stroke="#888"
                    />
                    <YAxis orientation="right" fontSize={11} stroke="#888" />
                    <Tooltip content={<MonthlyTooltip />} />
                    <Legend
                      wrapperStyle={{ paddingTop: "10px", fontSize: "12px" }}
                    />
                    <Area
                      type="monotone"
                      dataKey="issuedCount"
                      name="قبوض صادره"
                      stroke="#1A73E8"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorIssued)"
                    />
                    <Area
                      type="monotone"
                      dataKey="paidCount"
                      name="قبوض پرداخت‌شده"
                      stroke="#4caf50"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorPaid)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>

            {/* Chart 2: تفکیک وضعیت پرداخت قبوض */}
            <Grid item xs={12} lg={6}>
              <ChartCard
                title="وضعیت پرداخت قبوض عوارض"
                subtitle="نسبت قبوض پرداخت‌شده به قبوض در انتظار پرداخت"
              >
                <Grid container alignItems="center" sx={{ height: "100%" }}>
                  <Grid item xs={12} sm={7}>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={paymentStatusData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={65}
                          outerRadius={95}
                          paddingAngle={5}
                        >
                          {paymentStatusData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={entry.color || PIE_COLORS[index % PIE_COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip content={<PieTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <MDBox pr={2}>
                      {paymentStatusData.map((item, index) => (
                        <MDBox
                          key={index}
                          mb={2}
                          p={1.5}
                          borderRadius="8px"
                          sx={{
                            backgroundColor: index === 0 ? "#f1f8e9" : "#ffebee",
                            border: `1px solid ${item.color}40`,
                          }}
                        >
                          <MDBox display="flex" alignItems="center" mb={0.5}>
                            <MDBox
                              width={10}
                              height={10}
                              borderRadius="50%"
                              sx={{ backgroundColor: item.color }}
                              mr={1}
                            />
                            <MDTypography
                              variant="button"
                              fontWeight="bold"
                              fontSize="13px"
                            >
                              {item.name}
                            </MDTypography>
                          </MDBox>
                          <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <MDTypography variant="caption" color="text">
                              تعداد:
                            </MDTypography>
                            <MDTypography variant="button" fontWeight="bold">
                              {formatNumber(item.value)} ({formatNumber(item.percent)}%)
                            </MDTypography>
                          </MDBox>
                          <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mt={0.5}
                          >
                            <MDTypography variant="caption" color="text">
                              مبلغ:
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              color={index === 0 ? "success" : "error"}
                            >
                              {formatCurrency(item.amount)}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      ))}
                    </MDBox>
                  </Grid>
                </Grid>
              </ChartCard>
            </Grid>

            {/* Chart 3: تفکیک مبالغ بر اساس تعرفه‌های عوارض */}
            <Grid item xs={12} lg={6}>
              <ChartCard
                title="درآمد به تفکیک تعرفه‌های عوارض"
                subtitle="بیشترین تعرفه‌های مورد استفاده و درآمد حاصل از آن‌ها"
              >
                {tariffChartData.length === 0 ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height={280}
                  >
                    <MDTypography color="text" fontSize="14px">
                      داده‌ای برای نمایش تعرفه‌ها ثبت نشده است
                    </MDTypography>
                  </MDBox>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      data={tariffChartData}
                      margin={{ top: 15, right: 10, left: 10, bottom: 40 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="name"
                        interval={0}
                        angle={-25}
                        textAnchor="end"
                        fontSize={11}
                        stroke="#888"
                      />
                      <YAxis
                        orientation="right"
                        fontSize={11}
                        stroke="#888"
                        tickFormatter={(val) =>
                          val >= 1000000
                            ? `${(val / 1000000).toLocaleString("fa-IR")} م`
                            : val.toLocaleString("fa-IR")
                        }
                      />
                      <Tooltip content={<TariffTooltip />} />
                      <Bar
                        dataKey="totalAmount"
                        name="مبلغ کل (ریال)"
                        radius={[6, 6, 0, 0]}
                      >
                        {tariffChartData.map((entry, index) => (
                          <Cell
                            key={`tariff-cell-${index}`}
                            fill={BAR_COLORS[index % BAR_COLORS.length]}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </Grid>

            {/* Chart 4: مقایسه سالیانه صدور و وصول عوارض */}
            <Grid item xs={12} lg={6}>
              <ChartCard
                title="مقایسه سالیانه صدور و وصول عوارض"
                subtitle="بررسی تعداد قبوض صادره و وصول‌شده در سال‌های مختلف"
              >
                {yearlyChartData.length === 0 ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height={280}
                  >
                    <MDTypography color="text" fontSize="14px">
                      داده‌ای برای نمایش سال‌ها موجود نیست
                    </MDTypography>
                  </MDBox>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      data={yearlyChartData}
                      margin={{ top: 15, right: 10, left: 10, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="year" fontSize={12} stroke="#888" />
                      <YAxis orientation="right" fontSize={11} stroke="#888" />
                      <Tooltip content={<YearlyTooltip />} />
                      <Legend
                        wrapperStyle={{ paddingTop: "10px", fontSize: "12px" }}
                      />
                      <Bar
                        dataKey="totalBills"
                        name="کل قبوض صادره"
                        fill="#1A73E8"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="paidBills"
                        name="قبوض پرداخت‌شده"
                        fill="#4caf50"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </Grid>
          </Grid>
        </MDBox>
      )}
    </DashboardLayout>
  );
}
